import { MutationStatus } from "@tanstack/react-query";
import { VariantProps } from "class-variance-authority";
import React from "react";
import { Button, buttonVariants } from "./button/button";
import Spinner from "./spinner";

// Discriminated union for button type
// If type is 'submit' or undefined, onClick is not allowed
// If type is not 'submit', onClick is required

type SubmitButtonBaseProps = {
	mutationStatus: MutationStatus;
	children?: React.ReactNode;
	loadingText?: string;
	className?: string;
	variant?: VariantProps<typeof buttonVariants>["variant"];
	size?: VariantProps<typeof buttonVariants>["size"];
	disabled?: boolean;
	testId?: string;
};

type SubmitButtonSubmitType = {
	type?: "submit";
	onClick?: never;
};

type SubmitButtonButtonType = {
	type: Exclude<
		React.ButtonHTMLAttributes<HTMLButtonElement>["type"],
		"submit"
	>;
	onClick: React.MouseEventHandler<HTMLButtonElement>;
};

type Props = SubmitButtonBaseProps &
	(SubmitButtonSubmitType | SubmitButtonButtonType);

export default function SubmitButton({
	mutationStatus,
	children = "Submit",
	loadingText = "Submitting...",
	className,
	variant = "default",
	size = "default",
	testId,
	type = "submit",
	onClick,
	disabled,
}: Props) {
	const isLoading = mutationStatus === "pending";
	return (
		<Button
			type={type}
			variant={variant}
			size={size}
			data-testid={testId}
			className={className}
			disabled={isLoading || disabled}
			{...(type !== "submit" ? { onClick } : {})}
		>
			{isLoading ? (
				<div className="flex items-center justify-center space-x-2">
					<Spinner />
					{loadingText && <span>{loadingText}</span>}
				</div>
			) : (
				children
			)}
		</Button>
	);
}
